<template>
  <div>
    <v-overlay :value="isProcessing">
      <v-progress-circular
        indeterminate
        color="primary"
        :size="70"
        :width="7"
      ></v-progress-circular>
    </v-overlay>
    <VerifyCodeDialog
      :verificationType="emailVerificationType"
      :contactInfo="confirmedUser.email"
      :showDialog="showEmailCodeDialog"
      :onClose="closeEmailCodeDialog"
      :onSuccess="emailVerifiedSuccessfully"
      :challengeId="confirmedUser.challengeId"
      :onVerify="verifyRecoveryCode"
    />
    <v-row justify="center">
      <v-col lg="6" md="5" class="pb-0 hidden-sm-and-down">
        <v-img class="sticky-image" height="100vh" src="../../assets/register-dog.png" />
      </v-col>
      <v-col class="register" lg="6" md="7" sm="12">
        <div class="top-icons">
          <a
            class="back-icon"
            v-on:click="goBack"
          >
            <font-awesome-icon icon="long-arrow-alt-left" size="lg" /> Back
          </a>
        </div>
        <h1 class="register-title">Forgot Password</h1>        
        <div v-if="!verifiedEmail">
          <p>
            Type in your username below and click the button.<br/>
            <strong>We will confirm your identity by sending a verification code to the email address you provided to ASCA.</strong>
          </p>
          <p>
            <router-link to="/forgotusername">Forgot Username?</router-link>
          </p>
          <v-form class="search-form my-3">
            <v-text-field
              outlined
              prepend-inner-icon="mdi-magnify"
              v-model="searchValue"
              label="Enter Username"
              class="my-3"
              :disabled="verifiedEmail"
            ></v-text-field>        
            <v-btn
              :dark="isValid"
              large
              color="primary"
              v-on:click="onSearch"
              :disabled="verifiedEmail || !!searchValue == false"
            >Email me the code</v-btn>
          </v-form>
        </div>
        <div v-if="verifiedEmail">
          <v-form v-model="isValid" class="register-form">
            <v-text-field
              outlined
              required
              class="ma-1"
              label="New Password"
              type="password"
              :disabled="!verifiedEmail"
              v-model="newPassword"
              :rules="passwordRules"
            />
            <PasswordRules v-if="verifiedEmail" :password="newPassword" />
            <v-text-field
              outlined
              required
              class="ma-1"
              label="Confirm New Password"
              type="password"
              :disabled="!verifiedEmail"
              v-model="newPasswordConfirm"
              :rules="[
                (v) => !!v || 'Confirm password is required',
                (v) => v === newPassword || 'Passwords do not match'
              ]"
            />
          </v-form>
          <v-btn
            class="white--text"
            large
            v-on:click="onChangePassword"
            :dark="isValid && verifiedEmail"
            :disabled="!isValid || !verifiedEmail"
            color="primary">Reset password</v-btn>
      </div>
        <div class="my-3">
          <div v-if="!!alertType">
            <v-alert :type="alertType">
              {{ alertMessage }}
              <a class="white--text" v-if="alertType === 'success'" href="/login">
                Go to login
              </a>
            </v-alert>
          </div>
          <v-alert
            width="100%"
            v-model="isError"
            dismissible
            type="error"
          >{{ errorMessage }}</v-alert>
        </div>
        <div class="footer-container">
          <v-img height="2rem" contain src="../../assets/logo.svg" />
          <footer-text />
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script lang="ts">
import Vue from "vue";

import passwordValidators from '@/helpers/validate-password';

import personService from "@/services/portal/personService";
import { VerificationType } from "@/services/codeVerificationService";

import VerifyCodeDialog from "@/components/common/VerifyCodeDialog.vue";
import FooterText from '@/components/common/FooterText.vue';
import PasswordRules from '@/components/common/PasswordRules.vue';

export default Vue.extend({
  name: "ForgotPassword",
  components: {
    FooterText,
    VerifyCodeDialog,
    PasswordRules,
  },
  data: function () {
    return {
      isValid: false,
      isError: false,
      isProcessing: false,
      errorMessage: '',
      inputSearchText: false,
      verifiedEmail: false,
      showEmailCodeDialog: false,
      searchValue: "",
      confirmedUser: {} as any,
      newPassword: "",
      newPasswordConfirm: "",
      recoveryToken: "",
      alertType: "",
      alertMessage: "",
      passwordRules: passwordValidators,
    };
  },
  watch: {
    confirmedUser: function (newValue) {
      if (!!newValue.challengeId) this.showEmailCodeDialog = true;
    },
  },
  computed: {
    emailVerificationType: function () {
      return VerificationType.Email;
    },
  },
  methods: {
    goBack() { this.$router.back(); },
    closeEmailCodeDialog: function () {
      this.showEmailCodeDialog = false;
    },
    verifyRecoveryCode: personService.verifyAccountRecovery,
    emailVerifiedSuccessfully: function (response: any) {
      this.recoveryToken = response.recoveryToken;
      this.verifiedEmail = true;
      this.showEmailCodeDialog = false;
    },
    onSearch: async function () {
      this.inputSearchText = false;
      this.isProcessing = true;
      this.isError = false;
      this.confirmedUser = {};
      this.errorMessage = "";

      try {
        const response = await personService.getUserConfirmation(this.searchValue, false);

        if (response.success) {
          this.isError = false;
          this.confirmedUser = response;
        } else {
          this.isError = true;
          this.errorMessage = response.error;
        }
      } finally {
        this.isProcessing = false;
      }
    },
    onChangePassword: async function () {
      this.isProcessing = true;
      try {
        const response = await personService.forgotPassword(this.recoveryToken, this.newPassword);

        if (response.success) {
          this.alertType = "success";
          this.alertMessage = "Password updated successfully!";
        } else if (response.errorMessage) {
          this.alertType = "error";
          this.alertMessage = response.errorMessage;
        } else {
          this.alertType = "error";
          this.alertMessage = "Something unexpectedly went wrong.";
        }
      } catch (error) {
        this.alertType = "error";
        this.alertMessage = "Something unexpectedly went wrong.";
      } finally {
        this.isProcessing = false;
      }
    },
  },
});
</script>
